/**
 * @fileoverview FormField — the ONE labelled form field: label, control, hint, error and
 * disabled explanation, wired to each other.
 * @package @puffin-core/page-shell
 *
 * LEAVE-CANON-001 R14 F-R14-9 (owner rulings R-68 / R-69, 2026-09-27). Twenty-four local copies of
 * this component lived in pos-web, planner-web and prep-web, and they had drifted into four
 * measured defects: prep-web's demo form printed TWO asterisks (a literal `*` beside the
 * `.form-required` class, whose `::after` already adds one), a products copy's error had no
 * `role="alert"`, the customer-profile copy rendered a `<label>` linked to nothing, and the
 * category form relied on a native `required` that pre-empted the form's own validation and its
 * announced errors (FVA-001). This one field makes each of those impossible, and
 * `web/no-local-form-field` stops a new copy from being written.
 *
 * ## The contract
 *
 * The field OWNS its control's identity and description:
 *   - `id` goes on the control and the label's `htmlFor` points at it; the label itself is `<id>-label`,
 *     so a control that is not a form control (a contenteditable rich-text surface, which a `<label for>`
 *     cannot name) is named by `aria-labelledby="<id>-label"` instead;
 *   - `aria-describedby` names every line the field shows under the control (error, else hint;
 *     then the notice; then the disabled explanation), each with a stable id `<id>-error` /
 *     `-hint` / `-notice` / `-disabled`;
 *   - `aria-invalid` is set while an error SHOWS, and `aria-required` while `required`.
 *
 * ## The notice — an advisory line that is NOT an error (OWNER-RULINGS-WALK-BUILDS, 2026-10-07)
 *
 * `notice` is something the person should know about the value that does not make it wrong (the
 * product editors' "another product already uses this SKU"). It is a `role="status"` line: announced
 * politely, described to the control, never `aria-invalid`, and shown BESIDE the error or the hint
 * rather than instead of either. A field that takes a notice passes the prop on every render — `null`
 * while there is nothing to say — so the status region is in the tree BEFORE its text arrives; a live
 * region mounted together with its text is not reliably announced. While empty it carries no class
 * (`.form-hint` is a block with a top margin) and describes nothing.
 *
 * `children` is either a render function, called with exactly those control props
 * (`{ id, 'aria-describedby', 'aria-invalid', 'aria-required' }`), or ONE native `<input>` /
 * `<select>` / `<textarea>` element, which receives them through `cloneElement`: the child's own
 * `aria-describedby` is kept and ours appended, an `aria-invalid` / `aria-required` the child sets
 * explicitly wins, and the field's `id` always wins, because the label is linked to it. The element also gains its
 * canonical control class (`form-input` / `form-select` / `form-textarea`, by tag and input type), merged ahead of its
 * own classes and never twice (PAGE-SHELL-PRIMITIVES-001 B2-U3); a checkbox or radio `<input>` is refused, because
 * `<CheckboxField>` and `<RadioGroup>` are those fields.
 *
 * Anything else THROWS at render, because cloning cannot wire it honestly: a
 * layout box (`<div>` around an input and a spinner) would be labelled instead of the control
 * inside it, and a component decides for itself which props reach its control — page-shell's own
 * `<DateInput>` takes `ariaDescribedBy`, not `aria-describedby`, so a clone would drop the
 * description without a sound. Write those as a render function and map the props explicitly.
 *
 * The required marker is CSS-only (`.form-required::after`), never a literal asterisk in the
 * markup; the control's `aria-required` is what assistive technology reads. The field owns no
 * copy of its own, so it needs no translator.
 *
 * Introduces no CSS — class composition only (CSS-001): `.form-group`, `.form-group-compact`,
 * `.form-label`, `.form-required`, `.form-hint`, `.form-error`, `.form-field-disabled-message`
 * (`packages/shared-styles/css/utilities/forms.css`).
 */

import { cloneElement, isValidElement } from 'react';
import PropTypes from 'prop-types';

/**
 * Join the defined ids into one `aria-describedby` value.
 * @param {Array<string|undefined>} ids - Candidate ids, in reading order.
 * @returns {string|undefined} The space-joined ids, or undefined when none is defined.
 */
function joinIds(ids) {
  const defined = ids.filter(Boolean);
  return defined.length > 0 ? defined.join(' ') : undefined;
}

/** The `<input>` types that are a text box, and so take `.form-input` (a missing `type` is a text box too). */
const TEXT_BOX_TYPES = new Set([
  'text',
  'email',
  'url',
  'number',
  'search',
  'tel',
  'password',
  'date',
  'time',
  'datetime-local',
  'month',
  'week',
]);

/**
 * The canonical class of a native control, by its tag AND, for an `<input>`, its type: a select is `.form-select`, a
 * textarea `.form-textarea`, a text-box input `.form-input`. Any other input (file, hidden, range, color, image, the
 * buttons) has no canonical field class, so none is added; a checkbox or radio never reaches here (refused).
 *
 * @param {React.ReactElement} child - A native control element.
 * @returns {?string} The class, or null when the control has none.
 */
function canonicalControlClass(child) {
  if (child.type === 'select') return 'form-select';
  if (child.type === 'textarea') return 'form-textarea';
  const { type } = child.props;
  return type === undefined || TEXT_BOX_TYPES.has(type) ? 'form-input' : null;
}

/**
 * The child's class with its canonical control class MERGED in (PAGE-SHELL-PRIMITIVES-001 B2-U3, W3 A): first, ahead
 * of the child's own classes, which are kept in their order; never twice, so a child that already spells it keeps its
 * class string unchanged.
 *
 * @param {React.ReactElement} child - A native control element.
 * @returns {string|undefined} The class to render.
 */
function mergedControlClass(child) {
  const own = child.props.className;
  const canonical = canonicalControlClass(child);
  if (canonical === null) return own;
  const tokens = typeof own === 'string' ? own.split(/\s+/).filter(Boolean) : [];
  return tokens.includes(canonical) ? own : [canonical, ...tokens].join(' ');
}

/**
 * Hand the control props to ONE element child, keeping what the child states itself, and merge in its canonical
 * control class.
 * @param {React.ReactElement} child - The control element.
 * @param {Object} control - The field's control props.
 * @returns {React.ReactElement} The control, wired to the field.
 */
function wireElement(child, control) {
  const own = child.props;
  return cloneElement(child, {
    className: mergedControlClass(child),
    id: control.id,
    'aria-describedby': joinIds([own['aria-describedby'], control['aria-describedby']]),
    'aria-invalid': own['aria-invalid'] ?? control['aria-invalid'],
    'aria-required': own['aria-required'] ?? control['aria-required'],
  });
}

/** The native controls an element child may be; everything else takes a render function. */
const CONTROL_TAGS = new Set(['input', 'select', 'textarea']);

/**
 * The `<input>` types that are a BOX beside its words, never a field under a label: `<CheckboxField>` is the ONE
 * checkbox field and `<RadioGroup>` the ONE set of radios (PAGE-SHELL-PRIMITIVES-001 B2-U3).
 */
const BOX_TYPES = new Set(['checkbox', 'radio']);

/**
 * Why an element `children` cannot be wired, or null when it can. A layout box would be labelled
 * instead of its control, and a component may not forward the props a clone hands it, so both are
 * refused: write them as a render function. Checked at RENDER, not by prop-types — React 19 no
 * longer runs `propTypes`, so a prop-type validator here would be a check that never runs.
 * @param {*} child - The `children` prop, known not to be a function.
 * @returns {string|null}
 */
function elementChildRefusal(child) {
  if (!isValidElement(child)) {
    return 'FormField: `children` must be a render function or ONE native input, select or textarea element.';
  }
  if (typeof child.type !== 'string') {
    return "FormField: a component child may not forward the control props a clone hands it. Pass a render function and map them onto the component's own props.";
  }
  if (!CONTROL_TAGS.has(child.type)) {
    return `FormField: a <${child.type}> child would be labelled instead of the control inside it. Pass a render function and spread its props onto the control.`;
  }
  if (child.type === 'input' && BOX_TYPES.has(child.props.type)) {
    return `FormField: a ${child.props.type} <input> child is not a field FormField labels (the label sits above, the box is styled as a text box). Use <CheckboxField> for a checkbox and <RadioGroup> for radios.`;
  }
  return null;
}

/**
 * The field's own props. `data-testid` is added beside them on `FormField`'s parameter: a bracketed
 * `@param {string} [props['data-testid']]` is not a name the checker can read, and it silently dropped every
 * `@param` after it, `children` included, so every caller read as passing an unknown `children`.
 *
 * @typedef {object} FormFieldOwnProps
 * @property {string} id - The control's id; the label is linked to it.
 * @property {React.ReactNode} label - The label.
 * @property {boolean} [required] - Marks the label (CSS) and the control (`aria-required`).
 * @property {React.ReactNode} [error] - The validation error; shown with `role="alert"`.
 * @property {boolean} [showError=true] - Gate for the error (e.g. "only after a submit").
 * @property {React.ReactNode} [hint] - Helper text, shown while no error shows.
 * @property {React.ReactNode} [notice] - An advisory line (`role="status"`), or `null` while
 *   there is nothing to say; omit it for a field that never takes one.
 * @property {React.ReactNode} [disabledMessage] - Why the control is disabled.
 * @property {boolean} [compact] - Dense spacing for drawers and modals.
 * @property {string} [className] - Extra classes on the field group.
 * @property {string} [errorTestId] - `data-testid` on the error line.
 * @property {string} [hintTestId] - `data-testid` on the hint line.
 * @property {string} [noticeTestId] - `data-testid` on the notice line.
 * @property {Function|React.ReactElement} children - A render function called with the
 *   control props, or ONE native `<input>` / `<select>` / `<textarea>` that receives them.
 */

/**
 * The canonical labelled form field.
 *
 * @param {FormFieldOwnProps & {'data-testid'?: string}} props - `data-testid` lands on the field group.
 * @returns {React.JSX.Element} The field.
 */
export function FormField({
  id,
  label,
  required = false,
  error,
  showError = true,
  hint,
  notice,
  disabledMessage,
  compact = false,
  className,
  errorTestId,
  hintTestId,
  noticeTestId,
  'data-testid': testId,
  children,
}) {
  const errorShows = Boolean(showError && error);
  const hintShows = Boolean(hint) && !errorShows;
  const takesNotice = notice !== undefined;
  const noticeShows = Boolean(notice);
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const noticeId = `${id}-notice`;
  const disabledId = `${id}-disabled`;

  const control = {
    id,
    'aria-describedby': joinIds([
      errorShows ? errorId : undefined,
      hintShows ? hintId : undefined,
      noticeShows ? noticeId : undefined,
      disabledMessage ? disabledId : undefined,
    ]),
    'aria-invalid': errorShows ? true : undefined,
    'aria-required': required ? true : undefined,
  };

  if (typeof children !== 'function') {
    const refusal = elementChildRefusal(children);
    if (refusal) throw new Error(refusal);
  }

  const groupClass = ['form-group', compact ? 'form-group-compact' : null, className]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={groupClass} data-testid={testId}>
      <label
        htmlFor={id}
        id={`${id}-label`}
        className={required ? 'form-label form-required' : 'form-label'}
      >
        {label}
      </label>
      {typeof children === 'function' ? children(control) : wireElement(children, control)}
      {errorShows ? (
        <span id={errorId} className="form-error" role="alert" data-testid={errorTestId}>
          {error}
        </span>
      ) : null}
      {hintShows ? (
        <span id={hintId} className="form-hint" data-testid={hintTestId}>
          {hint}
        </span>
      ) : null}
      {takesNotice ? (
        <span
          id={noticeId}
          className={noticeShows ? 'form-hint' : undefined}
          role="status"
          data-testid={noticeTestId}
        >
          {noticeShows ? notice : null}
        </span>
      ) : null}
      {disabledMessage ? (
        <span id={disabledId} className="form-field-disabled-message">
          {disabledMessage}
        </span>
      ) : null}
    </div>
  );
}

FormField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.node.isRequired,
  required: PropTypes.bool,
  error: PropTypes.node,
  showError: PropTypes.bool,
  hint: PropTypes.node,
  notice: PropTypes.node,
  disabledMessage: PropTypes.node,
  compact: PropTypes.bool,
  className: PropTypes.string,
  errorTestId: PropTypes.string,
  hintTestId: PropTypes.string,
  noticeTestId: PropTypes.string,
  'data-testid': PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.element]).isRequired,
};

export default FormField;
